import { Box, Button, Stack } from '@mui/material';
import { TextField } from 'components/commons';
import Alert from 'components/commons/Alert';
import { PAGE_LIST } from 'constant/pages';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { saveKeyToLocalStorage } from 'utils/constant';
import { postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';

interface SettingFormType {
  url: string;
  appId: string;
  appKey: string;
  dbId: string;
  dbKey: string;
}

const SETTING_FORM_DEFAULT_VALUE = {
  url: process.env.REACT_APP_CLUSTER_DB_API ?? '',
  appId: '',
  appKey: '',
  dbId: '',
  dbKey: '',
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ISettingProps {}

const Setting: React.FC<ISettingProps> = () => {
  const navigate = useNavigate();

  const { control, handleSubmit, watch, setError } = useForm<SettingFormType>({
    defaultValues: SETTING_FORM_DEFAULT_VALUE,
  });
  const { url } = watch();

  const [configError, setConfigError] = useState<boolean>(false);

  const onSubmit = async (data: SettingFormType) => {
    try {
      await fetch(data.url + '/health');
      localStorage.setItem('url', url);
    } catch (error) {
      setError('url', {
        type: 'custom',
        message: ja.message_error.url_invalid,
      });
      return;
    }
    try {
      await postAuthLogin({
        appId: data.appId,
        appKey: data.appKey,
        dbId: data.dbId,
        dbKey: data.dbKey,
      });
      saveKeyToLocalStorage('appId', data.appId);
      saveKeyToLocalStorage('appKey', data.appKey);
      saveKeyToLocalStorage('dbId', data.dbId);
      saveKeyToLocalStorage('dbKey', data.dbKey);
      navigate(PAGE_LIST.HOME.pathname());
    } catch (error) {
      setConfigError(true);
    }
  };

  return (
    <Box
      sx={{
        width: '80vw',
        boxShadow: '0px 2px 4px 2px #00000030',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '20px',
        }}
      >
        <Stack gap='10px'>
          <Controller
            name='url'
            control={control}
            rules={{
              required: true,
              pattern: {
                value:
                  /^(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?/,
                message: ja.message_error.format_url,
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id='url'
                label={ja.setting.url.label}
                placeholder={ja.setting.url.placeholder}
                require
                error={fieldState.error}
              />
            )}
          />
          <Controller
            name='appId'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id='appId'
                label={ja.setting.app_id.label}
                placeholder={ja.setting.app_id.placeholder}
                require
                error={fieldState.error}
              />
            )}
          />
          <Controller
            name='appKey'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id='appKey'
                label={ja.setting.app_key.label}
                placeholder={ja.setting.app_key.placeholder}
                require
                error={fieldState.error}
              />
            )}
          />
          <Controller
            name='dbId'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id='dbId'
                label={ja.setting.db_id.label}
                placeholder={ja.setting.db_id.placeholder}
                require
                error={fieldState.error}
              />
            )}
          />
          <Controller
            name='dbKey'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id='dbKey'
                label={ja.setting.db_key.label}
                placeholder={ja.setting.db_key.placeholder}
                require
                error={fieldState.error}
              />
            )}
          />
          <Box sx={{ textAlign: 'right' }}>
            <Button
              type='submit'
              sx={{
                lineHeight: '35px',
              }}
            >
              {ja.setting.next}
            </Button>
          </Box>
        </Stack>
      </Box>
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Box>
  );
};

export default Setting;
